// #region: Class definition *********************************************************************/
/**
 * @class PasswordInput
 * @description A component to modify a password input so it can switch between show/hide its contents.
 * 
 * @extends WebComponent
 * Extends the base WebComponent class to provide password visibility toggle functionality.
 *
 * @example
 * <password-input>
 *    <input type="password" />
 * </password-input>
 *
 * @remarks
 *     The component expects a password input element to be nested inside it.
*/
 
class PasswordInput extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * The toggle button element for showing/hiding password.
     * @type {HTMLButtonElement|null}
     * 
     * @private
     */
    #button;

    /**
     * The icon element inside the toggle button.
     * @type {HTMLSpanElement|null}
     * 
     * @private
     */
    #icon;

    /**
     * The password input element.
     * @type {HTMLInputElement|null}
     * 
     * @private
     */
    #input;

    /**
     * Flag indicating whether the password is currently visible.
     * @type {boolean}
     * 
     * @private
     */
    #showPassword;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of PasswordInput.
     */
    constructor() {
        super();

        /* Whether to show the password. */
        this.#showPassword = false;

        // Get nested input.
        this.#input = this.querySelector('input[type="password"]');
        if (!this.#input) {
            console.error('No password input found inside <password-input>.');

            return;
        }

        // Create toggle button.
        this.#button = document.createElement('button');
        this.#button.type = 'button';
        this.#button.classList.add('button-icon-only', 'button-icon', 'toggle-password');
         
        this.#button.setAttribute('title', resources.identity.togglePasswordVisibility);
        this.#button.innerHTML = '<span class="lf-icon-visibility"></span>';

        this.#input.insertAdjacentElement('afterend', this.#button);
        this.#icon = this.#button.querySelector('span');
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof PasswordInput
     */
    static tagName = 'password-input';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof PasswordInput
     */
    toString() {
        return '[PasswordInput]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof PasswordInput
     */
    static toString() {
        return '[PasswordInput]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof PasswordInput
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when the custom element is attached to the DOM.
     * Sets up event listeners for the password toggle functionality.
     * 
     * @memberof PasswordInput
     */
    connectedCallback() {
        super.connectedCallback();

        // Events.
        this.#button.addEventListener('click', this.#togglePasswordVisibility.bind(this));
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * Removes event listeners to prevent memory leaks.
     * 
     * @memberof PasswordInput
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.#button.removeEventListener('click', this.#togglePasswordVisibility.bind(this));
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof PasswordInput
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Toggles password visibility (modifies icon and input type).
     * 
     * @private
     * @memberof PasswordInput
     */
    #togglePasswordVisibility() {
        this.#showPassword = !this.#showPassword;

        this.#icon.className = this.#showPassword ? 'lf-icon-visibility-off' : 'lf-icon-visibility';
        this.#input.type = this.#showPassword ? 'text' : 'password';
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register custom element.
 * Defines the custom element 'password-input' and maps it to the PasswordInput class.
 */
customElements.define(PasswordInput.tagName, PasswordInput);
